import { useCallback, useEffect, useRef, useState } from 'react'
import i18n from '@/i18n'
import { getMaxRecordingDurationSeconds } from '@/services/recordingConfig'
import {
	mapDisplayMediaFailure,
	mapGetUserMediaFailure,
	mediaRecorderSupportsPause,
	microphoneAudioConstraints,
	pickRecorderMime,
	requestMeetingAudioStream,
} from '@/utils/audioRecorderMedia'
import { captureUnexpectedError } from '@/utils/error'
import { fixWebmDuration } from '@/utils/fixWebmDuration'
import { readAnalyserMeter } from '@/utils/microphoneMeter'

export {
	getMeetingAudioCaptureSupport,
	mediaRecorderSupportsPause,
} from '@/utils/audioRecorderMedia'
export { MIC_PREVIEW_SIGNAL_DB_THRESHOLD } from '@/utils/microphoneMeter'

const ERR = 'pages.projectDetail.audioRecorder.errors'
const METER_FRAME_MS = 1000 / 30
const ANALYSER_FFT_SIZE = 1024

export type AudioRecorderState =
	| 'idle'
	| 'requesting'
	| 'recording'
	| 'paused'
	| 'stopped'
	| 'error'

export interface AudioRecorderResult {
	blob: Blob
	mimeType: string
	extension: string
	durationMs: number
}

export function useAudioRecorder() {
	const [state, setState] = useState<AudioRecorderState>('idle')
	const [elapsedMs, setElapsedMs] = useState(0)
	const [errorMessage, setErrorMessage] = useState<string | null>(null)
	const [result, setResult] = useState<AudioRecorderResult | null>(null)
	const [stoppedByLimit, setStoppedByLimit] = useState(false)
	const [inputDevices, setInputDevices] = useState<MediaDeviceInfo[]>([])
	const [selectedDeviceId, setSelectedDeviceId] = useState<string | null>(null)
	const [micPreviewLevelDb, setMicPreviewLevelDb] = useState(-100)
	const [micPreviewMeterPercent, setMicPreviewMeterPercent] = useState(4)
	const [micPreviewActive, setMicPreviewActive] = useState(false)
	const [micPreviewConnecting, setMicPreviewConnecting] = useState(false)
	const [micPreviewFailed, setMicPreviewFailed] = useState(false)
	const [includeMeetingAudio, setIncludeMeetingAudioState] = useState(false)
	const [meetingPreviewLevelDb, setMeetingPreviewLevelDb] = useState(-100)
	const [meetingPreviewMeterPercent, setMeetingPreviewMeterPercent] = useState(4)
	const [meetingPreviewActive, setMeetingPreviewActive] = useState(false)
	const [meetingPreviewConnecting, setMeetingPreviewConnecting] = useState(false)
	const [isMinimized] = useState(false)

	const mixedStreamRef = useRef<MediaStream | null>(null)
	const micStreamRef = useRef<MediaStream | null>(null)
	const displayStreamRef = useRef<MediaStream | null>(null)
	const recorderRef = useRef<MediaRecorder | null>(null)
	const chunksRef = useRef<Blob[]>([])
	const audioContextRef = useRef<AudioContext | null>(null)
	const micAnalyserRef = useRef<AnalyserNode | null>(null)
	const meetingAnalyserRef = useRef<AnalyserNode | null>(null)
	const micSourceRef = useRef<MediaStreamAudioSourceNode | null>(null)
	const meetingSourceRef = useRef<MediaStreamAudioSourceNode | null>(null)
	const meterRafRef = useRef<number | null>(null)
	const lastMeterTsRef = useRef(0)
	const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)
	const recordingStartedAtRef = useRef<number>(0)
	const pausedAccumMsRef = useRef(0)
	const pauseStartedAtRef = useRef<number | null>(null)
	const maxDurationMsRef = useRef(getMaxRecordingDurationSeconds() * 1000)
	const mimeChoiceRef = useRef(pickRecorderMime())
	const stoppedByLimitRef = useRef(false)
	const cancelledRef = useRef(false)
	const displayTrackEndedHandlerRef = useRef<(() => void) | null>(null)
	const micPreviewGenerationRef = useRef(0)
	const meetingPreviewGenerationRef = useRef(0)
	const stateRef = useRef<AudioRecorderState>('idle')

	useEffect(() => {
		stateRef.current = state
	}, [state])

	const refreshInputDevices = useCallback(async () => {
		if (typeof navigator === 'undefined' || !navigator.mediaDevices?.enumerateDevices) {
			return
		}
		try {
			const usableInputs = (devices: MediaDeviceInfo[]) =>
				devices.filter((d) => d.kind === 'audioinput' && d.deviceId !== '')

			let list = await navigator.mediaDevices.enumerateDevices()
			let inputs = usableInputs(list)

			const reportsAudioInputs = list.some((d) => d.kind === 'audioinput')
			if (reportsAudioInputs && inputs.length === 0) {
				try {
					const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
					for (const track of stream.getTracks()) {
						track.stop()
					}
					list = await navigator.mediaDevices.enumerateDevices()
					inputs = usableInputs(list)
				} catch {}
			}

			const byId = new Map<string, MediaDeviceInfo>()
			for (const d of inputs) {
				if (!byId.has(d.deviceId)) {
					byId.set(d.deviceId, d)
				}
			}
			inputs = [...byId.values()]

			setInputDevices(inputs)
			setSelectedDeviceId((prev) => {
				if (prev !== null && inputs.some((d) => d.deviceId === prev)) {
					return prev
				}
				return inputs[0]?.deviceId ?? null
			})
		} catch {}
	}, [])

	const stopMeterLoop = useCallback(() => {
		if (meterRafRef.current != null) {
			cancelAnimationFrame(meterRafRef.current)
			meterRafRef.current = null
		}
		lastMeterTsRef.current = 0
	}, [])

	const measureMeters = useCallback((timestamp: number) => {
		if (timestamp - lastMeterTsRef.current >= METER_FRAME_MS) {
			lastMeterTsRef.current = timestamp
			const micAnalyser = micAnalyserRef.current
			if (micAnalyser) {
				const { dbApprox, meterPercent } = readAnalyserMeter(micAnalyser)
				setMicPreviewLevelDb(dbApprox)
				setMicPreviewMeterPercent(meterPercent)
			}
			const meetingAnalyser = meetingAnalyserRef.current
			if (meetingAnalyser) {
				const { dbApprox, meterPercent } = readAnalyserMeter(meetingAnalyser)
				setMeetingPreviewLevelDb(dbApprox)
				setMeetingPreviewMeterPercent(meterPercent)
			}
		}
		meterRafRef.current = requestAnimationFrame(measureMeters)
	}, [])

	const startMeterLoop = useCallback(() => {
		if (meterRafRef.current != null) {
			return
		}
		meterRafRef.current = requestAnimationFrame(measureMeters)
	}, [measureMeters])

	const ensureAudioContext = useCallback(async () => {
		let ctx = audioContextRef.current
		if (!ctx || ctx.state === 'closed') {
			ctx = new AudioContext()
			audioContextRef.current = ctx
		}
		if (ctx.state === 'suspended') {
			await ctx.resume()
		}
		return ctx
	}, [])

	const connectMicAnalyser = useCallback(
		async (stream: MediaStream) => {
			const ctx = await ensureAudioContext()
			micSourceRef.current?.disconnect()
			const source = ctx.createMediaStreamSource(stream)
			const analyser = ctx.createAnalyser()
			analyser.fftSize = ANALYSER_FFT_SIZE
			source.connect(analyser)
			micSourceRef.current = source
			micAnalyserRef.current = analyser
			startMeterLoop()
		},
		[ensureAudioContext, startMeterLoop]
	)

	const connectMeetingAnalyser = useCallback(
		async (stream: MediaStream) => {
			const ctx = await ensureAudioContext()
			meetingSourceRef.current?.disconnect()
			const source = ctx.createMediaStreamSource(stream)
			const analyser = ctx.createAnalyser()
			analyser.fftSize = ANALYSER_FFT_SIZE
			source.connect(analyser)
			meetingSourceRef.current = source
			meetingAnalyserRef.current = analyser
			startMeterLoop()
		},
		[ensureAudioContext, startMeterLoop]
	)

	const detachDisplayTrackEndedHandler = useCallback(() => {
		const handler = displayTrackEndedHandlerRef.current
		if (!handler) {
			return
		}
		const audioTrack = displayStreamRef.current?.getAudioTracks()[0]
		if (audioTrack) {
			audioTrack.removeEventListener('ended', handler)
		}
		displayTrackEndedHandlerRef.current = null
	}, [])

	const clearMeetingAnalyser = useCallback(() => {
		meetingSourceRef.current?.disconnect()
		meetingSourceRef.current = null
		meetingAnalyserRef.current = null
		setMeetingPreviewLevelDb(-100)
		setMeetingPreviewMeterPercent(4)
	}, [])

	const handleMeetingShareEnded = useCallback(() => {
		// Share ended: drop meeting input only; MediaRecorder keeps running on the mic mix.
		detachDisplayTrackEndedHandler()
		displayStreamRef.current = null
		clearMeetingAnalyser()
		setMeetingPreviewActive(false)
		setMeetingPreviewConnecting(false)
	}, [clearMeetingAnalyser, detachDisplayTrackEndedHandler])

	const attachDisplayTrackEndedHandler = useCallback(
		(stream: MediaStream) => {
			detachDisplayTrackEndedHandler()
			const audioTrack = stream.getAudioTracks()[0]
			if (!audioTrack) {
				return
			}
			displayTrackEndedHandlerRef.current = handleMeetingShareEnded
			audioTrack.addEventListener('ended', handleMeetingShareEnded)
		},
		[detachDisplayTrackEndedHandler, handleMeetingShareEnded]
	)

	const stopMeetingPreview = useCallback(
		(options?: { stopTracks?: boolean }) => {
			const stopTracks = options?.stopTracks ?? true
			meetingPreviewGenerationRef.current += 1
			detachDisplayTrackEndedHandler()
			clearMeetingAnalyser()
			if (stopTracks) {
				for (const track of displayStreamRef.current?.getTracks() ?? []) {
					track.stop()
				}
				displayStreamRef.current = null
			}
			setMeetingPreviewActive(false)
			setMeetingPreviewConnecting(false)
		},
		[clearMeetingAnalyser, detachDisplayTrackEndedHandler]
	)

	const stopMicPreview = useCallback((options?: { stopTracks?: boolean }) => {
		const stopTracks = options?.stopTracks ?? true
		micPreviewGenerationRef.current += 1
		micSourceRef.current?.disconnect()
		micSourceRef.current = null
		micAnalyserRef.current = null
		if (stopTracks) {
			for (const track of micStreamRef.current?.getTracks() ?? []) {
				track.stop()
			}
			micStreamRef.current = null
		}
		setMicPreviewLevelDb(-100)
		setMicPreviewMeterPercent(4)
		setMicPreviewActive(false)
		setMicPreviewConnecting(false)
		setMicPreviewFailed(false)
	}, [])

	const cleanupAudioGraph = useCallback(() => {
		stopMeterLoop()
		micSourceRef.current?.disconnect()
		meetingSourceRef.current?.disconnect()
		micSourceRef.current = null
		meetingSourceRef.current = null
		micAnalyserRef.current = null
		meetingAnalyserRef.current = null
		const ctx = audioContextRef.current
		audioContextRef.current = null
		if (ctx && ctx.state !== 'closed') {
			void ctx.close()
		}
	}, [stopMeterLoop])

	const cleanupStream = useCallback(() => {
		detachDisplayTrackEndedHandler()
		for (const track of mixedStreamRef.current?.getTracks() ?? []) {
			track.stop()
		}
		mixedStreamRef.current = null
		for (const track of micStreamRef.current?.getTracks() ?? []) {
			track.stop()
		}
		micStreamRef.current = null
		for (const track of displayStreamRef.current?.getTracks() ?? []) {
			track.stop()
		}
		displayStreamRef.current = null
	}, [detachDisplayTrackEndedHandler])

	const stopTimer = useCallback(() => {
		if (timerRef.current != null) {
			clearInterval(timerRef.current)
			timerRef.current = null
		}
	}, [])

	const startMeetingPreview = useCallback(async () => {
		if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getDisplayMedia) {
			return
		}
		stopMeetingPreview()
		const generation = meetingPreviewGenerationRef.current
		setMeetingPreviewConnecting(true)
		try {
			const meetingResult = await requestMeetingAudioStream()
			if (meetingPreviewGenerationRef.current !== generation) {
				for (const track of meetingResult.stream.getTracks()) {
					track.stop()
				}
				return
			}
			if (!meetingResult.hasAudioTrack) {
				for (const track of meetingResult.stream.getTracks()) {
					track.stop()
				}
				setMeetingPreviewConnecting(false)
				setMeetingPreviewActive(false)
				return
			}

			const stream = meetingResult.stream
			displayStreamRef.current = stream
			attachDisplayTrackEndedHandler(stream)
			await connectMeetingAnalyser(stream)
			if (meetingPreviewGenerationRef.current !== generation) {
				for (const track of stream.getTracks()) {
					track.stop()
				}
				return
			}

			setMeetingPreviewConnecting(false)
			setMeetingPreviewActive(true)
		} catch (e: unknown) {
			if (meetingPreviewGenerationRef.current !== generation) {
				return
			}
			const { reportToSentry } = mapDisplayMediaFailure(e)
			if (reportToSentry) {
				captureUnexpectedError(e instanceof Error ? e : new Error('getDisplayMedia failed'))
			}
			setMeetingPreviewConnecting(false)
			setMeetingPreviewActive(false)
			setMeetingPreviewLevelDb(-100)
			setMeetingPreviewMeterPercent(4)
		}
	}, [attachDisplayTrackEndedHandler, connectMeetingAnalyser, stopMeetingPreview])

	const startMicPreview = useCallback(async () => {
		if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
			return
		}
		const recordingOrPaused = stateRef.current === 'recording' || stateRef.current === 'paused'
		if (recordingOrPaused) {
			return
		}
		stopMicPreview()
		const generation = micPreviewGenerationRef.current
		setMicPreviewConnecting(true)
		setErrorMessage(null)
		try {
			let stream: MediaStream
			try {
				stream = await navigator.mediaDevices.getUserMedia({
					audio: microphoneAudioConstraints(selectedDeviceId, {
						disableBuiltInProcessing: true,
					}),
				})
			} catch {
				stream = await navigator.mediaDevices.getUserMedia({
					audio: microphoneAudioConstraints(selectedDeviceId, {
						disableBuiltInProcessing: false,
					}),
				})
			}
			if (micPreviewGenerationRef.current !== generation) {
				for (const track of stream.getTracks()) {
					track.stop()
				}
				return
			}
			micStreamRef.current = stream
			void refreshInputDevices()
			await connectMicAnalyser(stream)
			if (micPreviewGenerationRef.current !== generation) {
				for (const track of stream.getTracks()) {
					track.stop()
				}
				return
			}
			setMicPreviewConnecting(false)
			setMicPreviewActive(true)
			setMicPreviewFailed(false)
			setErrorMessage(null)
			if (stateRef.current === 'error') {
				setState('idle')
			}
		} catch (e: unknown) {
			if (micPreviewGenerationRef.current !== generation) {
				return
			}
			const { message, reportToSentry } = mapGetUserMediaFailure(e)
			if (reportToSentry) {
				captureUnexpectedError(e instanceof Error ? e : new Error('getUserMedia failed'))
			}
			setMicPreviewConnecting(false)
			setMicPreviewFailed(true)
			setMicPreviewActive(false)
			setMicPreviewLevelDb(-100)
			setMicPreviewMeterPercent(4)
			setErrorMessage(message)
		}
	}, [connectMicAnalyser, refreshInputDevices, selectedDeviceId, stopMicPreview])

	const setIncludeMeetingAudio = useCallback(
		(enabled: boolean) => {
			setIncludeMeetingAudioState(enabled)
			if (!enabled) {
				stopMeetingPreview()
			}
		},
		[stopMeetingPreview]
	)

	const reset = useCallback(() => {
		stopTimer()
		stopMicPreview()
		stopMeetingPreview()
		cleanupAudioGraph()
		cleanupStream()
		recorderRef.current = null
		chunksRef.current = []
		setElapsedMs(0)
		setResult(null)
		setErrorMessage(null)
		setStoppedByLimit(false)
		stoppedByLimitRef.current = false
		cancelledRef.current = false
		pausedAccumMsRef.current = 0
		pauseStartedAtRef.current = null
		setIncludeMeetingAudioState(false)
		setState('idle')
	}, [cleanupAudioGraph, cleanupStream, stopMeetingPreview, stopMicPreview, stopTimer])

	useEffect(() => {
		return () => {
			stopTimer()
			stopMicPreview()
			stopMeetingPreview()
			cleanupAudioGraph()
			cleanupStream()
			const r = recorderRef.current
			if (r && (r.state === 'recording' || r.state === 'paused')) {
				cancelledRef.current = true
				r.stop()
			}
		}
	}, [cleanupAudioGraph, cleanupStream, stopMeetingPreview, stopMicPreview, stopTimer])

	const start = useCallback(async () => {
		if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
			setErrorMessage(i18n.t(`${ERR}.unsupportedBrowser`))
			setState('error')
			return
		}

		setErrorMessage(null)
		setResult(null)
		setStoppedByLimit(false)
		stoppedByLimitRef.current = false
		cancelledRef.current = false
		chunksRef.current = []
		maxDurationMsRef.current = getMaxRecordingDurationSeconds() * 1000
		mimeChoiceRef.current = pickRecorderMime()
		setState('requesting')

		try {
			let micStream = micStreamRef.current
			const liveTrack = micStream?.getAudioTracks().find((track) => track.readyState === 'live')
			const liveDeviceId = liveTrack?.getSettings().deviceId
			const micMatchesSelection =
				liveTrack != null &&
				(selectedDeviceId == null ||
					liveDeviceId == null ||
					liveDeviceId === '' ||
					liveDeviceId === selectedDeviceId)
			if (!micMatchesSelection) {
				await startMicPreview()
				micStream = micStreamRef.current
			}
			if (!micStream?.getAudioTracks().some((track) => track.readyState === 'live')) {
				setErrorMessage(i18n.t(`${ERR}.generic`))
				setState('error')
				return
			}

			let displayStream: MediaStream | null = null
			if (includeMeetingAudio) {
				const existingStream = displayStreamRef.current
				const hasLiveAudioTrack =
					existingStream?.getAudioTracks().some((track) => track.readyState === 'live') ?? false
				if (hasLiveAudioTrack && existingStream) {
					displayStream = existingStream
					attachDisplayTrackEndedHandler(existingStream)
				}
			}

			const audioContext = await ensureAudioContext()
			const micSource = micSourceRef.current
			if (!micSource) {
				setErrorMessage(i18n.t(`${ERR}.generic`))
				setState('error')
				return
			}
			// Reuse existing source nodes so we do not call createMediaStreamSource twice on the same stream.
			const destination = audioContext.createMediaStreamDestination()
			const meetingSource = includeMeetingAudio && displayStream ? meetingSourceRef.current : null
			const micGain = audioContext.createGain()
			micGain.gain.value = meetingSource ? 0.5 : 1
			micSource.connect(micGain)
			micGain.connect(destination)
			if (meetingSource) {
				const meetingGain = audioContext.createGain()
				meetingGain.gain.value = 0.5
				meetingSource.connect(meetingGain)
				meetingGain.connect(destination)
			}
			const recordingStream = destination.stream
			mixedStreamRef.current = recordingStream

			const { mimeType } = mimeChoiceRef.current
			const recorder =
				mimeType !== '' && MediaRecorder.isTypeSupported(mimeType)
					? new MediaRecorder(recordingStream, { mimeType })
					: new MediaRecorder(recordingStream)
			recorderRef.current = recorder

			recorder.ondataavailable = (ev) => {
				if (ev.data.size > 0) {
					chunksRef.current.push(ev.data)
				}
			}

			recorder.onstop = () => {
				stopTimer()
				cleanupAudioGraph()
				cleanupStream()
				recorderRef.current = null
				if (cancelledRef.current) {
					cancelledRef.current = false
					reset()
					return
				}
				const recordedMime = recorder.mimeType || mimeType
				const finalMime = (recordedMime.split(';')[0] || recordedMime || 'audio/webm').trim()
				const ext: 'webm' | 'm4a' = finalMime === 'audio/mp4' ? 'm4a' : 'webm'
				const blob = new Blob(chunksRef.current, { type: recordedMime || finalMime })
				const durationMs = Math.max(0, Date.now() - recordingStartedAtRef.current)

				const finish = (finalBlob: Blob) => {
					setResult({
						blob: finalBlob,
						mimeType: finalMime,
						extension: ext,
						durationMs,
					})
					setState('stopped')
				}

				if (finalMime === 'audio/webm') {
					fixWebmDuration(blob, durationMs)
						.then(finish)
						.catch((e) => {
							captureUnexpectedError(e)
							finish(blob)
						})
				} else {
					finish(blob)
				}
			}

			recordingStartedAtRef.current = Date.now()
			pausedAccumMsRef.current = 0
			pauseStartedAtRef.current = null

			recorder.start(250)
			startMeterLoop()

			timerRef.current = setInterval(() => {
				const now = Date.now()
				let elapsed = now - recordingStartedAtRef.current - pausedAccumMsRef.current
				if (pauseStartedAtRef.current != null) {
					elapsed -= now - pauseStartedAtRef.current
				}
				setElapsedMs(Math.max(0, elapsed))
				if (
					elapsed >= maxDurationMsRef.current &&
					!stoppedByLimitRef.current &&
					(recorder.state === 'recording' || recorder.state === 'paused')
				) {
					stoppedByLimitRef.current = true
					setStoppedByLimit(true)
					recorder.stop()
				}
			}, 200)

			setState('recording')
		} catch (e: unknown) {
			cleanupAudioGraph()
			cleanupStream()
			const { message, reportToSentry } = mapGetUserMediaFailure(e)
			if (reportToSentry) {
				captureUnexpectedError(e instanceof Error ? e : new Error('getUserMedia failed'))
			}
			setErrorMessage(message)
			setState('error')
		}
	}, [
		attachDisplayTrackEndedHandler,
		cleanupAudioGraph,
		cleanupStream,
		ensureAudioContext,
		includeMeetingAudio,
		reset,
		selectedDeviceId,
		startMeterLoop,
		startMicPreview,
		stopTimer,
	])

	const pause = useCallback(() => {
		const r = recorderRef.current
		if (!r || r.state !== 'recording' || !mediaRecorderSupportsPause()) {
			return
		}
		r.pause()
		pauseStartedAtRef.current = Date.now()
		setState('paused')
	}, [])

	const resume = useCallback(() => {
		const r = recorderRef.current
		if (!r || r.state !== 'paused') {
			return
		}
		if (pauseStartedAtRef.current != null) {
			pausedAccumMsRef.current += Date.now() - pauseStartedAtRef.current
			pauseStartedAtRef.current = null
		}
		r.resume()
		setState('recording')
	}, [])

	const stop = useCallback(() => {
		const r = recorderRef.current
		if (!r || (r.state !== 'recording' && r.state !== 'paused')) {
			return
		}
		r.stop()
	}, [])

	const cancel = useCallback(() => {
		const r = recorderRef.current
		cancelledRef.current = true
		if (r && (r.state === 'recording' || r.state === 'paused')) {
			r.stop()
			return
		}
		stopTimer()
		cleanupAudioGraph()
		cleanupStream()
		reset()
	}, [cleanupAudioGraph, cleanupStream, reset, stopTimer])

	const requestMinimize = useCallback(() => {
		// TODO(minimize-bar): lift recorder state above the route; collapsed Pause/Stop bar
	}, [])

	return {
		state,
		elapsedMs,
		micPreviewLevelDb,
		micPreviewMeterPercent,
		micPreviewActive,
		micPreviewConnecting,
		micPreviewFailed,
		includeMeetingAudio,
		setIncludeMeetingAudio,
		meetingPreviewLevelDb,
		meetingPreviewMeterPercent,
		meetingPreviewActive,
		meetingPreviewConnecting,
		isMinimized,
		requestMinimize,
		errorMessage,
		result,
		stoppedByLimit,
		inputDevices,
		selectedDeviceId,
		setSelectedDeviceId,
		refreshInputDevices,
		startMicPreview,
		stopMicPreview,
		startMeetingPreview,
		start,
		pause,
		resume,
		stop,
		cancel,
		reset,
	}
}
